import { Navigate, Routes, Route, useLocation } from 'react-router-dom'
import store from 'store'

import { GoogleCallback } from 'components/authentication/callbacks/Google'
import { SAML as SAMLCallback } from 'components/authentication/callbacks/SAML'
import { FindCompany as ResetCompany } from 'components/authentication/FindCompany'
import { LoginWithForesightPromo } from 'components/authentication/foresight-promo/LoginWithForesightPromo'
import { NewUser } from 'components/authentication/registration/new-user/NewUser'
import { Register } from 'components/authentication/registration/register-company/Register'
import { AccountSetup } from 'components/authentication/registration/self-service/AccountSetup'
import { RequestPasswordReset } from 'components/authentication/RequestPasswordReset'
import { ResetPassword } from 'components/authentication/ResetPassword'
import { SignIn } from 'components/authentication/SignIn'
import { SignInUsingSAML } from 'components/authentication/SignInUsingSAML'
import { TrackedGremlinRouteElement } from 'components/TrackedGremlinRouteElement'
import { isPrivateEdition } from 'utils/app-config'

/**
 * This route handles the callback from the identity provider.
 * This is exported for use in authenticated routes because we want
 * the same behavior there as well. See EN-9010
 * Provided as a definition because react router doesn't allow `Route`
 * to be defined in a separate component.
 */
export const SAML_CALLBACK_ROUTE_DEFINITION = {
  element: (
    <TrackedGremlinRouteElement
      component={<SAMLCallback />}
      name="SAML Callback"
    />
  ),
  path: 'sso/saml/acs'
}

function LoginIndex() {
  return (
    <Routes>
      <Route
        element={
          <TrackedGremlinRouteElement
            component={
              <LoginWithForesightPromo>
                <SignIn />
              </LoginWithForesightPromo>
            }
            name="Login"
          />
        }
        path=""
      />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<SignInUsingSAML />}
            name="SAML Login"
          />
        }
        path="saml"
      />
    </Routes>
  )
}

function UsersIndex() {
  return (
    <Routes>
      {!isPrivateEdition() && (
        <Route
          element={
            <TrackedGremlinRouteElement
              component={<GoogleCallback />}
              name="Google Callback"
            />
          }
          path="sso/google/login"
        />
      )}
      <Route {...SAML_CALLBACK_ROUTE_DEFINITION} />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<ResetPassword />}
            name="Reset Password"
          />
        }
        path="password/update"
      />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<NewUser />}
            name="New User Setup"
          />
        }
        path="new"
      />
    </Routes>
  )
}

export function UnauthenticatedRoutes() {
  const location = useLocation()

  const locationPathname = location.pathname

  /**
   * When arriving at an authenticated route, this will store the current location to be used on the login
   * page if the user is redirected there.
   *
   * The query string is part of the destination, not decoration. `/oauth/consent?request_id=...`
   * is meaningless without it: the user would return from login to a consent screen that cannot
   * say which authorization request it is answering, and the third party waiting on the callback
   * would simply never hear back. Team deep links are still collapsed to the team list, because
   * the specific team may not be one the user can see once they sign in.
   */
  const sanitizedPathname = locationPathname.includes('/settings/team/')
    ? '/settings/teams'
    : `${locationPathname}${location.search}`
  // Compare the path, not the stored value. The stored value now carries the query string, so an
  // exact match against '/login' stopped excluding the login page itself as soon as it had any
  // query parameters -- storing /login?x=y and navigating back to it after signing in.
  if (locationPathname !== '/login') {
    store.set('loginRedirect', sanitizedPathname)
  }

  return (
    <Routes>
      <Route element={<LoginIndex />} path="/login/*" />
      <Route element={<UsersIndex />} path={'/users/*'} />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<AccountSetup />}
            name="Account Setup"
          />
        }
        path="/setup"
      />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<RequestPasswordReset />}
            name="Password Reset Request"
          />
        }
        path="/password-reset"
      />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<ResetCompany />}
            name="Reset Company"
          />
        }
        path="/find/company"
      />
      <Route
        element={
          <TrackedGremlinRouteElement
            component={<Register />}
            name="Register"
          />
        }
        path="/register"
      />
      <Route
        element={
          // redirect any non-matching routes
          <Navigate replace to="/login" />
        }
        path="*"
      />
    </Routes>
  )
}
